
export const getPrimaryRoles = () => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  const nonSystemRoles = settings.roles.filter((r) => !r.isSystem);
  return sortRolesByHierarchy(nonSystemRoles).map((r) => r.name);
};

export const getPrimaryRolesForPropertyType = (propertyType) => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  if (!propertyType) return getPrimaryRoles();
  const pType = String(propertyType).toLowerCase();
  const isHybrid = pType.includes("hybrid") || pType === "mixed";

  const matchingRoles = settings.roles
    .filter((r) => !r.isSystem)
    .filter((r) => {
      if (!Array.isArray(r.propertyTypes) || !r.propertyTypes.length) return true;
      if (isHybrid) {
        return r.propertyTypes.some((pt) => {
          const lower = pt.toLowerCase();
          return lower === "residential" || lower === "commercial" || lower.includes("hybrid");
        });
      }
      return r.propertyTypes.some((pt) => {
        const lower = pt.toLowerCase();
        return lower === pType || (pType !== "industrial" && lower.includes("hybrid"));
      });
    });

  return sortRolesByHierarchy(matchingRoles, propertyType).map((r) => r.name);
};

export const getSecondaryRoles = () => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  return settings.roles.filter((r) => !r.isSystem).map((r) => r.name);
};

export const getSecondaryRolesFull = () => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  return settings.roles.filter((r) => !r.isSystem);
};

export const getSystemRoles = () => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  return settings.roles.filter((r) => r.isSystem).map((r) => r.name);
};

export const getScopes = () => {
  const settings = getCachedAppSettings() || defaultAppSettings;
  return settings.scopes || defaultAppSettings.scopes;
};

import api from "./api";
import API_ROUTES from "./apiRoutes";
import { getCurrentRoleKey } from "./authService";
import { compareRolesByHierarchy, sortRolesByHierarchy } from "../utils/roleHierarchy";

export const APP_SETTINGS_EVENT = "myasmita:settings-updated";
export const RESIDENT_ROLE_SCOPES = ["resident"];
export const defaultRoleRestrictions = {
  canManageUsers: false,
  canManageAuthentication: false,
  canManageVisitors: false,
  canManagePropertyDetails: false,
  canManageMembers: false,
  canManageDomesticStaff: false,
  canManageNotices: false,
  canManageFormsPolicies: false,
  canManageServiceBookings: false,
  canManagePayments: false,
  canManageFeedback: false,
  canManagePolling: false,
  canManageVehicleTracking: false,
  canManageGlobalSettings: false,
  canViewSystemLogs: false,
  canDelegate: false,
};

export const defaultAppSettings = {
  scopes: [
    {
      id: "resident",
      name: "Resident",
      description: "Flat residents, owners, tenants, and residential occupants.",
      propertyTypes: ["Residential", "Hybrid (Residential + Commercial)"],
      isSystem: true,
    },
    {
      id: "operations",
      name: "Operations & Facility",
      description: "On-ground staff: security, cleaning, maintenance, vendors.",
      propertyTypes: ["Residential", "Commercial", "Hybrid (Residential + Commercial)", "Industrial"],
      isSystem: true,
    },
    {
      id: "management",
      name: "Committee & Management",
      description: "Committee members, society admins, property managers.",
      propertyTypes: ["Residential", "Commercial", "Hybrid (Residential + Commercial)", "Industrial"],
      isSystem: true,
    },
    {
      id: "corporate",
      name: "Corporate & Tenant Entity",
      description: "Office occupants, unit managers, warehouse and logistics staff.",
      propertyTypes: ["Commercial", "Hybrid (Residential + Commercial)", "Industrial"],
      isSystem: true,
    },
    {
      id: "platform",
      name: "Platform & IT",
      description: "Platform-wide administration, finance, marketing, and support.",
      propertyTypes: [],
      isSystem: true,
    },
  ],
  roles: [
    // ──────────────────────────────────────────────
    // SYSTEM / PLATFORM ROLES
    // ──────────────────────────────────────────────
    {
      id: "super-admin",
      name: "Super Admin",
      power: 100,
      propertyTypes: [],
      scope: "platform",
      description: "Master administrator with unrestricted CRUD access across all platform modules and properties.",
      isSystem: true,
      roleType: "system",
      restrictions: {
        canManageUsers: true,
        canManageAuthentication: true,
        canManageVisitors: true,
        canManagePropertyDetails: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: true,
        canManageFormsPolicies: true,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canManageGlobalSettings: true,
        canViewSystemLogs: true,
      },
    },
    {
      id: "admin",
      name: "Admin",
      power: 90,
      propertyTypes: [],
      scope: "platform",
      description: "Operational admin with access to core management tools.",
      isSystem: true,
      roleType: "system",
      restrictions: {
        canManageUsers: true,
        canManageAuthentication: false,
        canManageVisitors: true,
        canManagePropertyDetails: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: true,
        canManageFormsPolicies: true,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canManageGlobalSettings: true,
        canViewSystemLogs: true,
      },
    },
    {
      id: "it",
      name: "IT Admin",
      power: 80,
      propertyTypes: [],
      scope: "platform",
      description: "Platform IT and technical operations managing security, authentication, logs, and configurations.",
      isSystem: true,
      roleType: "system",
      restrictions: {
        canManageUsers: true,
        canManageAuthentication: true,
        canManagePropertyDetails: true,
        canViewSystemLogs: true,
      },
    },
    {
      id: "finance",
      name: "Finance Admin",
      power: 75,
      propertyTypes: [],
      scope: "platform",
      description: "Platform-wide financial and billing management overseeing payments, invoices, and accounting.",
      isSystem: true,
      roleType: "system",
      restrictions: {
        canManagePayments: true,
      },
    },
    {
      id: "marketing",
      name: "Marketing Admin",
      power: 70,
      propertyTypes: [],
      scope: "platform",
      description: "Platform marketing, notices, announcements, and user engagement polling.",
      isSystem: true,
      roleType: "system",
      restrictions: {
        canManageNotices: true,
        canManagePolling: true,
      },
    },
    // ──────────────────────────────────────────────
    // RESIDENTIAL / STANDARD PROPERTY ROLES
    // ──────────────────────────────────────────────
    {
      id: "owner",
      name: "Owner",
      power: 90,
      propertyTypes: ["Residential", "Hybrid (Residential + Commercial)"],
      scope: "resident",
      description: "Property unit owner with full resident access and flat ownership.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
      },
    },
    {
      id: "tenant",
      name: "Tenant",
      power: 80,
      propertyTypes: ["Residential", "Hybrid (Residential + Commercial)"],
      scope: "resident",
      description: "Property unit tenant / leasing occupant.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
      },
    },
    {
      id: "guard",
      name: "Security Guard",
      power: 20,
      propertyTypes: ["Residential", "Commercial", "Hybrid (Residential + Commercial)", "Industrial"],
      scope: "operations",
      description: "Gate security guard managing visitor check-ins and logs.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Tower",
      restrictions: {
        canManageVisitors: true,
        canManageVehicleTracking: true,
      },
    },
    // ──────────────────────────────────────────────
    // CORPORATE & INDUSTRIAL ROLES
    // ──────────────────────────────────────────────
    {
      id: "director",
      name: "Director",
      power: 90,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "management",
      description: "Executive Director / Board Member with delegation authority for visitor approvals and boardroom reservations.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageUsers: true,
        canManageVisitors: true,
        canManagePropertyDetails: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: true,
        canManageFormsPolicies: true,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canDelegate: true,
      },
    },
    {
      id: "plant-manager",
      name: "Plant Manager",
      power: 85,
      propertyTypes: ["Industrial"],
      scope: "management",
      description: "Plant & factory operations manager overseeing industrial unit operations.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canDelegate: true,
      },
    },
    {
      id: "commercial-owner",
      name: "Commercial Owner",
      power: 90,
      propertyTypes: ["Commercial", "Hybrid (Residential + Commercial)"],
      scope: "corporate",
      description: "Owner of commercial office, shop, or retail space.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canDelegate: true,
      },
    },
    {
      id: "commercial-tenant",
      name: "Commercial Tenant",
      power: 80,
      propertyTypes: ["Commercial", "Hybrid (Residential + Commercial)"],
      scope: "corporate",
      description: "Leasing tenant of commercial office or retail unit.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canDelegate: true,
      },
    },
    {
      id: "office-manager",
      name: "Office Manager",
      power: 75,
      propertyTypes: ["Commercial", "Hybrid (Residential + Commercial)"],
      scope: "corporate",
      description: "Manager overseeing corporate office operations, staff, visitors, and deliveries.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageMembers: true,
        canManageDomesticStaff: true,
        canManageNotices: false,
        canManageServiceBookings: true,
        canManagePayments: true,
        canManageFeedback: true,
        canManagePolling: true,
        canManageVehicleTracking: true,
        canDelegate: true,
      },
    },
    {
      id: "hod",
      name: "Head of Department (HOD)",
      power: 70,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "management",
      description: "Senior Department Leadership (HOD, General Manager, CFO) supervising department operations, managing team leads, and approving departmental visitor requests.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Floor",
      restrictions: {
        canManageVisitors: true,
        canManageNotices: true,
        canManageServiceBookings: true,
        canManageFeedback: true,
        canDelegate: true,
      },
    },
    {
      id: "team-lead",
      name: "Team Lead (TL)",
      power: 60,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "management",
      description: "Department Team Lead / Supervisor. Supervises team members; receives and acts on visitor entries when team members are absent (TL absence fallback routing).",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Floor",
      restrictions: {
        canManageVisitors: true,
        canManageServiceBookings: true,
        canManageFeedback: true,
        canDelegate: true,
      },
    },
    {
      id: "employee",
      name: "Employee",
      power: 50,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "corporate",
      description: "Corporate employee across departments. Can create visitor invites, reserve meeting rooms, receive arrival alerts, and request peon/facility services.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Flat",
      restrictions: {
        canManageVisitors: true,
        canManageServiceBookings: true,
        canManageFeedback: true,
        canDelegate: true,
      },
    },
    {
      id: "floor-receptionist",
      name: "Floor Receptionist",
      power: 40,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "operations",
      description: "Floor-level receptionist on Executive floors (Floor 1 & 2) holding delegated authority to approve/reject visitors and allocate meeting rooms on behalf of Directors.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Floor",
      restrictions: {
        canManageVisitors: true,
        canManageFeedback: true,
        canManageNotices: true,
        canDelegate: false,
      },
    },
    {
      id: "gate-receptionist",
      name: "Gate Receptionist",
      power: 35,
      propertyTypes: ["Industrial", "Commercial"],
      scope: "operations",
      description: "Main gate receptionist responsible for initial visitor intake, badge printing, vehicle logging, and routing entry requests.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Tower",
      restrictions: {
        canManageVisitors: true,
        canManageVehicleTracking: true,
        canManageNotices: true,
        canDelegate: false,
      },
    },
    {
      id: "support-staff",
      name: "Support Staff",
      power: 10,
      propertyTypes: ["Industrial", "Commercial", "Residential"],
      scope: "operations",
      description: "Support personnel (Peons, Housekeeping, Electricians, Pantry Cook) fulfilling on-demand desk service requests.",
      isSystem: false,
      roleType: "standard",
      assignmentLevel: "Floor",
      restrictions: {
        canManageServiceBookings: true,
        canManageFeedback: true,
      },
    },
  ],
  restrictions: {
    allowSelfRegistration: false,
    requireInviteForAdmins: true,
    restrictSocietyCreationToAdmins: true,
    requireApprovalForResidentAccounts: true,
    allowRoleEditingForSocietyAdmins: false,
    enableVisitorAndSecurityRoles: true,
  },
  residentSpecificRoles: [],
};

let settingsCache = null;

const normalizeRoleKey = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

const getDefaultRole = (role = {}) => {
  const needle = normalizeRoleKey(role.id || role.name);
  if (!needle) return null;

  return (
    defaultAppSettings.roles.find((defaultRole) => {
      const normalizedId = normalizeRoleKey(defaultRole.id);
      const normalizedName = normalizeRoleKey(defaultRole.name);
      return normalizedId === needle || normalizedName === needle;
    }) || null
  );
};

const normalizeRole = (role = {}) => {
  const defaultRole = getDefaultRole(role);
  const baseRestrictions = defaultRole?.restrictions || {};

  let inferredRoleType = role.roleType || defaultRole?.roleType;
  if (!inferredRoleType) {
    if (role.isSystem) {
      inferredRoleType = "system";
    } else {
      inferredRoleType = "standard";
    }
  }

  // Ensure propertyTypes is always an array
  let propertyTypes = role.propertyTypes;
  if (!Array.isArray(propertyTypes)) {
    if (role.propertyType) {
      propertyTypes = [role.propertyType];
    } else if (defaultRole?.propertyTypes) {
      propertyTypes = defaultRole.propertyTypes;
    } else {
      propertyTypes = inferredRoleType === "system" ? ["Residential", "Commercial", "Industrial"] : ["Residential"];
    }
  }

  return {
    ...(defaultRole || {}),
    ...role,
    power: Number(role.power != null ? role.power : (defaultRole?.power ?? 50)),
    roleType: inferredRoleType,
    propertyTypes,
    scope: role.scope || defaultRole?.scope || "resident",
    restrictions: {
      ...defaultRoleRestrictions,
      ...baseRestrictions,
      ...(role.restrictions || {}),
    },
  };
};

const normalizeSettings = (settings = defaultAppSettings) => {
  const mergedRolesMap = new Map();
  defaultAppSettings.roles.forEach(r => mergedRolesMap.set(r.id, r));

  if (settings.roles && Array.isArray(settings.roles)) {
    settings.roles.forEach(r => mergedRolesMap.set(r.id, { ...mergedRolesMap.get(r.id), ...r }));
  }

  return {
    scopes: settings.scopes && Array.isArray(settings.scopes) && settings.scopes.length > 0
      ? settings.scopes
      : defaultAppSettings.scopes,
    roles: Array.from(mergedRolesMap.values())
      .map(normalizeRole)
      .sort((a, b) => compareRolesByHierarchy(a, b)),
    restrictions: {
      ...defaultAppSettings.restrictions,
      ...(settings.restrictions || {}),
    },
    residentSpecificRoles: settings.residentSpecificRoles || defaultAppSettings.residentSpecificRoles,
  };
};

const emitSettingsUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(APP_SETTINGS_EVENT));
  }
};

export const getAppSettings = async () => {
  try {
    if (settingsCache) {
      return settingsCache;
    }

    const roleKey = getCurrentRoleKey();
    if (roleKey !== "admin" && roleKey !== "super-admin") {
      settingsCache = normalizeSettings(defaultAppSettings);
      return settingsCache;
    }

    const res = await api.get(API_ROUTES.settings.base);
    settingsCache = normalizeSettings(res.data);
    return settingsCache;
  } catch (error) {
    console.error("GET APP SETTINGS ERROR:", error);
    return settingsCache || normalizeSettings(defaultAppSettings);
  }
};

export const getCachedAppSettings = () => settingsCache;

export const saveAppSettings = async (settings) => {
  const payload = normalizeSettings(settings);
  const res = await api.put(API_ROUTES.settings.base, payload);
  settingsCache = normalizeSettings(res.data);
  emitSettingsUpdated();
  return settingsCache;
};

export const getRoleNames = async () => {
  try {
    const roleKey = getCurrentRoleKey();
    if (roleKey !== "admin" && roleKey !== "super-admin") {
      return (settingsCache || normalizeSettings(defaultAppSettings)).roles.map((role) => role.name);
    }

    const res = await api.get(API_ROUTES.settings.roles);
    const roles = res.data.map(normalizeRole);
    settingsCache = {
      ...(settingsCache || normalizeSettings(defaultAppSettings)),
      roles,
    };
    return roles.map((role) => role.name);
  } catch (error) {
    console.error("GET ROLE NAMES ERROR:", error);
    return (settingsCache || normalizeSettings(defaultAppSettings)).roles.map(
      (role) => role.name
    );
  }
};

export const getDefaultRestrictions = () => ({
  ...defaultAppSettings.restrictions,
});

export const isResidentRole = (role) =>
  RESIDENT_ROLE_SCOPES.includes(String(role?.scope || "").toLowerCase());

export const getCommitteeRoles = (settings) => {
  const currentSettings = settings || settingsCache || defaultAppSettings;
  return (currentSettings.residentSpecificRoles || [])
    .filter((role) => role.is_committee)
    .map((role) => role.name);
};

export const getBasicRoles = (settings) => {
  const currentSettings = settings || settingsCache || defaultAppSettings;
  return (currentSettings.residentSpecificRoles || [])
    .filter((role) => !role.is_committee)
    .map((role) => role.name);
};

export const getRoleAssignmentLevel = (settings, roleName) => {
  const currentSettings = settings || settingsCache || defaultAppSettings;
  const role = currentSettings.roles?.find(
    (r) => String(r.name).toLowerCase() === String(roleName).toLowerCase()
  );
  if (role && role.assignmentLevel) {
    return role.assignmentLevel;
  }
  const roleNameLower = String(roleName).toLowerCase();
  if (roleNameLower === "owner" || roleNameLower === "tenant") {
    return "Flat";
  }
  return "Tower";
};